import { lazy } from "react";
import { withLazyRoute } from "@/components/loading/LazyRouteWrapper";

import {
  Home as HomeIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  AccountBalance as AccountBalanceIcon,
  Email as EmailIcon,
  RssFeed as RssFeedIcon,
  Draw as DrawIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Hub as HubIcon,
  Hail as HailIcon,
  Work as WorkIcon,
  ViewKanban as ViewKanbanIcon,
  EventAvailable as EventAvailableIcon,
  NotificationsActive as NotificationsActiveIcon,
  Message as MessageIcon,
} from "@mui/icons-material";
// import {
//   UserManagement,
//   ClientManagement,
//   FreelancerManagement,
//   AgreementManagement,
//   BlogWriter,
//   CSAManagement,
//   EmailManagement,
//   AdminHome,
//   Configuration,
//   FreelancerOverview,
//   TimesheetManagement,
//   ClientOverview,
//   ReviewsManager,
//   JobPoster,
//   JobPosterEditor,
//   AuditLog,
//   AdminFinancialHub,
//   NotificationManagement,
//   BlogPostEditor,
//   SchedulingManagement,
//   SMSManagement,
// } from "@/views/Admin";
const AdminHome = withLazyRoute(
  lazy(() => import("@/views/Dashboards/Admin")),
  "Loading admin home..."
);
const AdminFinancialHub = withLazyRoute(
  lazy(() => import("@/views/Admin/AdminFinancialHub")),
  "Loading financial hub..."
);
const ClientOverview = withLazyRoute(
  lazy(() => import("@/views/Admin/ClientManagement/ClientOverview")),
  "Loading client overview..."
);
const ClientManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/ClientManagement")),
  "Loading client management..."
);
const FreelancerOverview = withLazyRoute(
  lazy(() => import("@/views/Admin/FreelancerManagement/FreelancerOverview")),
  "Loading freelancer overview..."
);
const FreelancerManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/FreelancerManagement")),
  "Loading freelancer management..."
);
const AgreementManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/AgreementManagement")),
  "Loading agreement management..."
);
const TimesheetManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/TimesheetManagement")),
  "Loading timesheet management..."
);
const CSAManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/CSAManagement")),
  "Loading CSA management..."
);
const BlogWriter = withLazyRoute(
  lazy(() => import("@/views/Admin/BlogWriter")),
  "Loading blog writer..."
);
const BlogPostEditor = withLazyRoute(
  lazy(() => import("@/views/Admin/BlogWriter/BlogPostEditor")),
  "Loading blog editor..."
);
const JobPoster = withLazyRoute(
  lazy(() => import("@/views/Admin/JobPoster")),
  "Loading job poster..."
);
const JobPosterEditor = withLazyRoute(
  lazy(() => import("@/views/Admin/JobPoster/JobPosterEditor")),
  "Loading job editor..."
);
const ReviewsManager = withLazyRoute(
  lazy(() => import("@/views/Admin/ReviewsManager")),
  "Loading reviews manager..."
);
const AuditLog = withLazyRoute(
  lazy(() => import("@/views/Admin/AuditLog")),
  "Loading audit log..."
);
const Configuration = withLazyRoute(
  lazy(() => import("@/views/Admin/Configuration")),
  "Loading configuration..."
);
const SchedulingManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/SchedulingManagement")),
  "Loading scheduling management..."
);
const NotificationManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/NotificationManagement")),
  "Loading notification management..."
);
const SMSManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/SMSManagement")),
  "Loading SMS management..."
);
const UserManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/UserManagement")),
  "Loading user management..."
);
const EmailManagement = withLazyRoute(
  lazy(() => import("@/views/Admin/EmailManagement")),
  "Loading email management..."
);
const BotDetailsView = withLazyRoute(
  lazy(
    () =>
      import("@/views/Admin/SchedulingManagement/BotDetailsView/BotDetailsView")
  ),
  "Loading bot details..."
);

// const TimesheetInput = withLazyRoute(
//   lazy(() => import("@/components/features/timetracking/TimesheetInput")),
//   "Loading timesheet input..."
// );

import {
  Navigation,
  NavigationItem,
  NavigationPageItem,
} from "types/navigation";
// import {
//   FreeloHome,
//   FreeloBoard,
//   TimeTracker,
//   MyMeetingsView,
//   SharedMeetingDetails,
// } from "@/views/Shared";
const TimeTracker = withLazyRoute(
  lazy(() =>
    import("views/Shared/TimeTracker").then((m) => ({ default: m.TimeTracker }))
  ),
  "Loading time tracker..."
);
const FreeloHome = withLazyRoute(
  lazy(() =>
    import("views/Shared/FreeloHome").then((m) => ({ default: m.FreeloHome }))
  ),
  "Loading freelancer home..."
);
const FreeloBoard = withLazyRoute(
  lazy(() => import("views/Shared/FreeloBoard")),
  "Loading freelancer board..."
);
const MyMeetingsView = withLazyRoute(
  lazy(() =>
    import("views/Shared/MyMeetings/MyMeetingsView").then((m) => ({
      default: m.MyMeetingsView,
    }))
  ),
  "Loading meetings view..."
);
const SharedMeetingDetails = withLazyRoute(
  lazy(() =>
    import(
      "views/Shared/MyMeetings/SharedMeetingDetails/SharedMeetingDetails"
    ).then((m) => ({ default: m.SharedMeetingDetails }))
  ),
  "Loading meeting details..."
);
import { AdminPermissionClaims } from "@freetech/models/user";

interface PagePermissionRequirement {
  category: keyof AdminPermissionClaims;
  action: string;
  value?: any; // For specific permission values
}

const PAGE_PERMISSIONS: Record<string, PagePermissionRequirement | null> = {
  admin: null, // Home page - always accessible to admins
  "admin/tracker": {
    category: "timetracker",
    action: "freelancer_or_admin_view",
  },
  freelo: { category: "freelo", action: "view" },
  "admin/financial-hub": { category: "financialHub", action: "view" },
  "admin/client-management": { category: "clients", action: "view" },
  "admin/freelancer-management": { category: "freelancers", action: "view" },
  "admin/agreement-management": {
    category: "projectAssignments",
    action: "view",
  },
  "admin/timesheet-management": { category: "timesheets", action: "view" },
  "admin/csa-management": { category: "projectAssignments", action: "view" }, // CSA is related to assignments
  "blog-writer": { category: "blogs", action: "view" },
  "job-poster": { category: "jobs", action: "view" },
  "reviews-manager": { category: "reviews", action: "view" },
  "email-management": { category: "email_management", action: "view" },
  "admin/user-management": { category: "user_management", action: "view" },
  "audit-log": { category: "audit_log", action: "view" },
  "admin/configuration": { category: "system", action: "view" },
  "admin/scheduling-management": { category: "scheduling", action: "view" },
};

const hasPagePermission = (
  segment: string,
  permissions: AdminPermissionClaims | null
): boolean => {
  if (!permissions) return false;

  const requirement = PAGE_PERMISSIONS[segment];
  if (!requirement) return true; // No permission required (like home page)

  const { category, action, value } = requirement;
  const categoryPermissions = permissions[category];

  if (!categoryPermissions) return false;

  const permissionValue =
    categoryPermissions[action as keyof typeof categoryPermissions];

  if (typeof permissionValue === "boolean") {
    return permissionValue === true;
  } else if (typeof permissionValue === "string") {
    return permissionValue !== "none" && permissionValue !== "";
  } else if (Array.isArray(permissionValue)) {
    return true;
  }

  return false;
};

const filterNavigationByPermissions = (
  items: Navigation,
  permissions: AdminPermissionClaims | null
): Navigation => {
  const filteredItems: Navigation = [];

  for (const item of items) {
    if (item.kind === "header" || item.kind === "divider") {
      filteredItems.push(item);
    } else {
      const pageItem = item as NavigationPageItem;
      if (
        pageItem.segment &&
        hasPagePermission(pageItem.segment, permissions)
      ) {
        filteredItems.push(item);
      } else if (pageItem.children) {
        const filteredChildren = filterNavigationByPermissions(
          pageItem.children,
          permissions
        );
        if (filteredChildren.length > 0) {
          filteredItems.push({
            ...pageItem,
            children: filteredChildren,
          });
        }
      }
    }
  }

  const cleanedItems: Navigation = [];
  let pendingHeader: NavigationItem | null = null;
  let pendingDivider: NavigationItem | null = null;
  let hasPagesSinceLastHeader = false;

  for (let i = 0; i < filteredItems.length; i++) {
    const item = filteredItems[i];

    if (item.kind === "header") {
      if (pendingHeader && hasPagesSinceLastHeader) {
        cleanedItems.push(pendingHeader);
        if (pendingDivider) {
          cleanedItems.push(pendingDivider);
        }
      }

      pendingHeader = item;
      pendingDivider = null;
      hasPagesSinceLastHeader = false;
    } else if (item.kind === "divider") {
      pendingDivider = item;
    } else {
      if (pendingHeader && !hasPagesSinceLastHeader) {
        cleanedItems.push(pendingHeader);
        if (pendingDivider) {
          cleanedItems.push(pendingDivider);
        }
        pendingHeader = null;
        pendingDivider = null;
      }

      cleanedItems.push(item);
      hasPagesSinceLastHeader = true;
    }
  }

  return cleanedItems;
};

export const superuserPages: Navigation = [
  {
    kind: "page",
    title: "Home",
    segment: "admin",
    icon: <HomeIcon />,
    component: <AdminHome />,
  },
  {
    segment: "admin/tracker",
    title: "Time Tracker",
    icon: <AccessTimeIcon />,
    component: <TimeTracker />,
    // component: <TimesheetInput />,
  },
  // {
  //   segment: "admin/my-meetings",
  //   title: "My Meetings",
  //   icon: <AccessTimeIcon />,
  //   component: <MyMeetingsView />,
  //   slugBabyPattern: ":meetingId",
  //   slugBabyComponent: <SharedMeetingDetails />,
  // },
  {
    kind: "page",
    title: "Freelo",
    segment: "freelo",
    icon: <ViewKanbanIcon />,
    component: <FreeloHome />,
    slugBabyPattern: ":boardId",
    slugBabyComponent: <FreeloBoard />,
  },

  { kind: "header", title: "Operations" },
  { kind: "divider" },
  {
    segment: "admin/financial-hub",
    title: "Financial Hub",
    icon: <AccountBalanceIcon />,
    component: <AdminFinancialHub />,
  },
  {
    segment: "admin/client-management",
    title: "Clients",
    icon: <HubIcon />,
    slugBabyPattern: ":clientId",
    slugBabyComponent: <ClientOverview />,
    component: <ClientManagement />,
  },
  {
    segment: "admin/freelancer-management",
    title: "Freelancers",
    icon: <HailIcon />,
    slugBabyPattern: ":freelancerId",
    slugBabyComponent: <FreelancerOverview />,
    component: <FreelancerManagement />,
  },

  {
    segment: "admin/agreement-management",
    title: "Assignment Manager",
    icon: <AssignmentIcon />,
    component: <AgreementManagement />,
  },
  {
    segment: "admin/timesheet-management",
    title: "Timesheet Manager",
    icon: <AccessTimeIcon />,
    component: <TimesheetManagement />,
  },

  {
    segment: "admin/csa-management",
    title: "CSA Manager",
    icon: <DrawIcon />,
    component: <CSAManagement />,
    slugBabyPattern: ":csaId",
    slugBabyComponent: <BlogPostEditor />,
  },
  {
    segment: "admin/scheduling-management",
    title: "Scheduling Management",
    icon: <EventAvailableIcon />,
    component: <SchedulingManagement />,
    slugBabyPattern: "bots/:botId",
    slugBabyComponent: <BotDetailsView />,
  },
  { kind: "header", title: "Marketing" },
  { kind: "divider" },
  {
    segment: "blog-writer",
    title: "Blog Writer",
    icon: <RssFeedIcon />,
    component: <BlogWriter />,
    slugBabyPattern: ":blogId",
    slugBabyComponent: <BlogPostEditor />,
  },
  {
    segment: "job-poster",
    title: "Job Poster",
    icon: <WorkIcon />,
    component: <JobPoster />,
    slugBabyPattern: ":jobId",
    slugBabyComponent: <JobPosterEditor />,
  },
  {
    segment: "reviews-manager",
    title: "Reviews Manager",
    icon: <VisibilityIcon />,
    component: <ReviewsManager />,
  },
  {
    segment: "email-management",
    title: "Email Management",
    icon: <EmailIcon />,
    component: <EmailManagement />,
  },
  {
    segment: "admin/sms-management",
    title: "SMS Management",
    icon: <MessageIcon />,
    component: <SMSManagement />,
  },
  { kind: "header", title: "Admin" },
  { kind: "divider" },

  {
    segment: "admin/user-management",
    title: "User Management",
    icon: <GroupIcon />,
    component: <UserManagement />,
  },
  {
    segment: "audit-log",
    title: "Audit Log",
    icon: <AssignmentIcon />,
    component: <AuditLog />,
  },
  {
    segment: "admin/configuration",
    title: "Configuration",
    icon: <SettingsIcon />,
    component: <Configuration />,
  },

  {
    segment: "notification-management",
    title: "Notification Management",
    icon: <NotificationsActiveIcon />,
    component: <NotificationManagement />,
  },
];

/**
 * Get super user pages filtered by user permissions
 * @param permissions - User's admin permissions
 * @returns Filtered navigation array based on permissions
 */
export const getSuperUserPages = (
  permissions: AdminPermissionClaims
): Navigation => {
  return filterNavigationByPermissions(superuserPages, permissions);
};
